import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { roundTo2Decimals } from '@principle-theorem/accounting';
import { CurrentScopeFacade } from '@principle-theorem/ng-principle-shared';
import {
  formControlChanges$,
  MOMENT_DATEPICKER_PROVIDERS,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { TransactionAllocation } from '@principle-theorem/principle-core';
import { PatientPermissions } from '@principle-theorem/principle-core/features';
import {
  IInvoice,
  type IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  type INamedDocument,
  mergeDayAndTime,
  snapshot,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import * as moment from 'moment-timezone';
import { type Moment } from 'moment-timezone';
import { type Observable, of } from 'rxjs';
import { determineNewCreatedAtDate } from '../../transaction-helpers';
import {
  bindLimitToAmountControl,
  createPractitionerLimit$,
} from '../../transaction-amount-limit';
import { AmendTransactionDateStore } from '../transaction-edit-dialog/amend-transaction-date.store';
import { TransactionPracticeOptions } from '../transaction-practice-options';

export interface ITransactionAmountFormOptions {
  enableDateReceived: boolean;
  enableCurrentPractice: boolean;
  enableAssignment?: boolean;
  initialAmount?: number;
  disableAmount?: boolean;
  isRefund?: boolean;
}

export interface ITransactionAmountDialogData {
  title: string;
  max?: number;
  practice: INamedDocument<IPractice>;
  invoice: WithRef<IInvoice>;
  options: Partial<ITransactionAmountFormOptions>;
}

export interface ITransactionAmountDialogResult {
  amount: number;
  dateReceived: Moment;
  practiceRef: DocumentReference<IPractice>;
  attributedTo?: DocumentReference<IStaffer>;
}

@Component({
  selector: 'pr-transaction-amount-dialog',
  templateUrl: './transaction-amount-dialog.component.html',
  styleUrls: ['./transaction-amount-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS, AmendTransactionDateStore],
  standalone: false,
})
export class TransactionAmountDialogComponent {
  form: TypedFormGroup<ITransactionAmountDialogResult>;
  accountAdminPermission = PatientPermissions.AccountInvoiceAdmin;
  practices$: Observable<INamedDocument<IPractice>[]>;
  practiceOptions: TransactionPracticeOptions;
  options: ITransactionAmountFormOptions;
  staff: INamedDocument<IStaffer>[];
  showAttributedTo: boolean;
  limit$: Observable<number>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ITransactionAmountDialogData,
    private _dialogRef: MatDialogRef<
      TransactionAmountDialogComponent,
      ITransactionAmountDialogResult
    >,
    public amendDateStore: AmendTransactionDateStore,
    private _currentScope: CurrentScopeFacade,
    private _destroyRef: DestroyRef
  ) {
    this.options = {
      enableCurrentPractice: false,
      enableDateReceived: false,
      enableAssignment: false,
      initialAmount: data.max,
      ...data.options,
    };

    const initialAmount = this.options.initialAmount
      ? roundTo2Decimals(this.options.initialAmount)
      : undefined;
    const amountValidators = compact([
      Validators.required,
      data.max ? Validators.max(data.max) : undefined,
    ]);
    this.form = new TypedFormGroup<ITransactionAmountDialogResult>({
      amount: new TypedFormControl<number>(initialAmount, amountValidators),
      dateReceived: new TypedFormControl<Moment>(moment()),
      practiceRef: new TypedFormControl<DocumentReference<IPractice>>(),
      attributedTo: new TypedFormControl<
        DocumentReference<IStaffer> | undefined
      >(),
    });

    if (data.options.disableAmount) {
      this.form.controls.amount.disable();
    }

    this.practiceOptions = new TransactionPracticeOptions(
      this._currentScope.currentPractice$,
      this.data.practice,
      this.options.enableCurrentPractice
    );
    this.practiceOptions.initialValue$
      .pipe(takeUntilDestroyed())
      .subscribe((practice) =>
        this.form.controls.practiceRef.setValue(practice.ref)
      );

    const { staff, showAttributedTo } =
      TransactionAllocation.getAttributedToConfig(this.data.invoice);
    this.staff = staff;
    this.showAttributedTo = showAttributedTo;

    this.limit$ = this.options.isRefund
      ? of(data.max ?? 0)
      : createPractitionerLimit$(
          formControlChanges$(this.form.controls.attributedTo),
          this.data.invoice,
          { baseLimit: data.max }
        );

    bindLimitToAmountControl(
      this.limit$,
      this.form.controls.amount,
      this._destroyRef
    );
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    const data = this.form.getRawValue();

    const dateReceived = data.dateReceived
      ? await determineNewCreatedAtDate(
          { createdAt: toTimestamp(), practiceRef: data.practiceRef },
          mergeDayAndTime(data.dateReceived, moment()),
          await snapshot(this.amendDateStore.minDate$),
          await snapshot(this.amendDateStore.maxDate$)
        )
      : moment();

    this._dialogRef.close({
      ...this.form.getRawValue(),
      dateReceived,
    });
  }
}
